import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CHILD_OF,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_MEMBER,
  RELATION_HAS_PART,
  RELATION_MEMBER_OF,
  RELATION_OWNED_BY,
  RELATION_OWNER_OF,
  RELATION_PARENT_OF,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  DependencyGraph,
  DependencyGraphTypes,
} from '@backstage/core-components';
import { errorApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  EntityInfoCard,
  catalogApiRef,
  entityPresentationSnapshot,
  entityRouteRef,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { ARCHIVED_TAG } from '../../input/ArchivedPicker';
import { EntityGraphNode } from './EntityGraphNode';

/**
 * The two names the catalog gives one and the same relation, the first one
 * being the direction the edge is drawn in. Both names are written on the edge,
 * so a `childOf` of the entity on screen reads as the `parentOf` it is.
 */
const RELATION_PAIRS: string[][] = [
  [RELATION_OWNER_OF, RELATION_OWNED_BY],
  [RELATION_CONSUMES_API, RELATION_API_CONSUMED_BY],
  [RELATION_API_PROVIDED_BY, RELATION_PROVIDES_API],
  [RELATION_HAS_PART, RELATION_PART_OF],
  [RELATION_PARENT_OF, RELATION_CHILD_OF],
  [RELATION_HAS_MEMBER, RELATION_MEMBER_OF],
  [RELATION_DEPENDS_ON, RELATION_DEPENDENCY_OF],
];

/** Everything the graph reads off a related entity. */
const ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  // Not drawn, but read: an archived neighbour is left out, see isArchived.
  'metadata.tags',
  'spec.type',
  'spec.profile.displayName',
];

/**
 * Whether an entity has been wound up - a department or team that was closed,
 * a product that was retired.
 *
 * The graph is about how things stand, so an archived neighbour is not drawn:
 * the relation outlives the entity, and a department that shut down two years
 * ago sits in the tree looking like any other team of its parent. The same
 * goes everywhere else in the app, where archived entities are out of the
 * listing until the switch is turned on - see ArchivedPicker.
 *
 * The entity the page is on is drawn whether or not it is archived. It is the
 * one the reader asked for, and a graph of an entity without that entity in it
 * would be empty.
 */
function isArchived(entity: Entity): boolean {
  return entity.metadata.tags?.includes(ARCHIVED_TAG) ?? false;
}

type RelationNode = {
  id: string;
  entity: Entity;
  title: string;
  focused: boolean;
  onClick: () => void;
};

type RelationEdge = {
  from: string;
  to: string;
  label: string;
  relations: string[];
};

export interface RelationsGraphCardProps {
  /** Heading of the card, `Relations` by default. */
  title?: string;
  /** Height of the graph area in pixels. */
  height?: number;
  /** Kinds an entity has to be of to be drawn, all of them by default. */
  kinds?: string[];
  /** Relations that are followed, all of them by default. */
  relations?: string[];
  /**
   * Whether a related entity is drawn, all of them by default. Named and
   * applied like the one of the catalog graph card; it has to be a stable
   * function, a new one on every render re-fetches the neighbours.
   */
  entityFilter?: (entity: Entity) => boolean;
  direction?: DependencyGraphTypes.Direction;
}

const useLabelStyles = makeStyles(
  theme => ({
    text: {
      fill: theme.palette.textContrast,
    },
    secondary: {
      fill: theme.palette.textSubtle,
    },
  }),
  { name: 'RtlDataRelationsGraphLabel' },
);

/** Both names of the relation, `parentOf / childOf`, on the edge. */
function RelationGraphLabel({
  edge,
}: DependencyGraphTypes.RenderLabelProps<RelationEdge>) {
  const classes = useLabelStyles();
  return (
    <text className={classes.text} textAnchor="middle">
      {edge.relations.map((relation, index) => (
        <tspan key={relation} className={index % 2 ? classes.secondary : ''}>
          {index > 0 && <tspan> / </tspan>}
          {relation}
        </tspan>
      ))}
    </text>
  );
}

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  graph: {
    width: '100%',
    flex: 1,
    minHeight: 0,
    // There is no way to style the edges between the nodes, so the same hack
    // the catalog graph uses is used here.
    '& path[marker-end]': {
      transition: 'filter 0.1s ease-in-out',
    },
    '& path[marker-end]:hover': {
      filter: `drop-shadow(2px 2px 4px ${theme.palette.primary.dark});`,
    },
    '& g[data-testid=label]': {
      transition: 'transform 0s',
    },
  },
  progress: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    marginLeft: '-20px',
    marginTop: '-20px',
  },
}));

/**
 * The entity on screen and everything one relation away from it.
 *
 * The catalog graph of the catalog-graph plugin does the same, but it hands its
 * nodes to dagre in the order the catalog answered in, which is the order of
 * the entity refs - and those are CMDB vuids that say nothing about the label a
 * node is drawn with. Sorting nodes and edges by that label is the reason this
 * card exists; it is also why it only goes one relation deep, which is all the
 * entity pages ask for.
 */
export function RelationsGraphCard(props: RelationsGraphCardProps) {
  const {
    title = 'Relations',
    height = 400,
    kinds,
    relations,
    entityFilter,
    direction = DependencyGraphTypes.Direction.LEFT_RIGHT,
  } = props;
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const theme = useTheme();
  const classes = useStyles();

  // The filters arrive as array literals from the entity pages, a new one on
  // every render, so they are kept as strings to compare by.
  const kindKey = kinds?.join(',') ?? '';
  const relationKey = relations?.join(',') ?? '';
  const kindPrefixes = useMemo(
    () =>
      kindKey
        ? kindKey.split(',').map(kind => `${kind.toLocaleLowerCase('en-US')}:`)
        : undefined,
    [kindKey],
  );
  const relationTypes = useMemo(
    () => (relationKey ? new Set(relationKey.split(',')) : undefined),
    [relationKey],
  );

  const rootRef = stringifyEntityRef(entity);
  // Every entity the root is related to, with the relations that lead to it.
  const targets = useMemo(() => {
    const byRef = new Map<string, string[]>();
    for (const relation of entity.relations ?? []) {
      if (relationTypes && !relationTypes.has(relation.type)) {
        continue;
      }
      if (
        kindPrefixes &&
        !kindPrefixes.some(prefix => relation.targetRef.startsWith(prefix))
      ) {
        continue;
      }
      const types = byRef.get(relation.targetRef);
      if (!types) {
        byRef.set(relation.targetRef, [relation.type]);
      } else if (!types.includes(relation.type)) {
        types.push(relation.type);
      }
    }
    return byRef;
  }, [entity, kindPrefixes, relationTypes]);

  const {
    value: relatedEntities,
    loading,
    error,
  } = useAsync(async () => {
    const entityRefs = [...targets.keys()];
    if (entityRefs.length === 0) {
      return [];
    }
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs,
      fields: ENTITY_FIELDS,
    });
    // A relation can point at an entity that is not in the catalog (yet), that
    // one is left out rather than drawn as an empty box. So is one that has
    // been archived, see isArchived, and one the page filters out.
    return items.filter(
      (item): item is Entity =>
        !!item && !isArchived(item) && (entityFilter?.(item) ?? true),
    );
  }, [catalogApi, targets, entityFilter]);

  useEffect(() => {
    if (error) {
      errorApi.post(error);
    }
  }, [errorApi, error]);

  const { nodes, edges } = useMemo(() => {
    const openEntity = (entityRef: string) => () => {
      const { kind, namespace, name } = parseEntityRef(entityRef);
      navigate(
        entityRoute({
          kind: kind.toLocaleLowerCase('en-US'),
          namespace: namespace.toLocaleLowerCase('en-US'),
          name,
        }),
      );
    };
    const toNode = (it: Entity, focused: boolean): RelationNode => ({
      id: stringifyEntityRef(it),
      entity: it,
      title: entityPresentationSnapshot(it).primaryTitle,
      focused,
      onClick: openEntity(stringifyEntityRef(it)),
    });
    const graphNodes = [
      toNode(entity, true),
      ...(relatedEntities ?? []).map(it => toNode(it, false)),
    ];
    const titles = new Map(graphNodes.map(it => [it.id, it.title]));
    // Dagre places a rank in the order it walks it: the nodes in the order they
    // were handed over, and from there the targets of a node in the order its
    // edges were. Both therefore have to be sorted for the graph to read
    // alphabetically.
    const byTitle = (a: string, b: string) =>
      (titles.get(a) ?? a).localeCompare(titles.get(b) ?? b, 'en-US');
    graphNodes.sort((a, b) => byTitle(a.id, b.id));

    const graphEdges = new Map<string, RelationEdge>();
    for (const node of graphNodes) {
      if (node.id === rootRef) {
        continue;
      }
      for (const type of targets.get(node.id) ?? []) {
        const pair = RELATION_PAIRS.find(
          ([forward, back]) => forward === type || back === type,
        ) ?? [type];
        // The second name of a pair is the relation seen from the other end, so
        // the edge points at the root instead of away from it.
        const backwards = pair[1] === type;
        const from = backwards ? node.id : rootRef;
        const to = backwards ? rootRef : node.id;
        const known =
          graphEdges.get(`${from}>${to}`) ?? graphEdges.get(`${to}>${from}`);
        if (known) {
          known.relations = [...new Set([...known.relations, ...pair])];
        } else {
          graphEdges.set(`${from}>${to}`, {
            from,
            to,
            // Without a label no label element is rendered at all.
            label: 'visible',
            relations: pair,
          });
        }
      }
    }

    return {
      nodes: graphNodes,
      edges: [...graphEdges.values()].sort(
        (a, b) => byTitle(a.from, b.from) || byTitle(a.to, b.to),
      ),
    };
  }, [entity, relatedEntities, targets, rootRef, entityRoute, navigate]);

  return (
    <EntityInfoCard title={title}>
      <div className={classes.container} style={{ height }}>
        {loading && <CircularProgress className={classes.progress} />}
        <DependencyGraph<RelationNode, RelationEdge>
          nodes={nodes}
          edges={edges}
          renderNode={EntityGraphNode}
          renderLabel={RelationGraphLabel}
          direction={direction}
          className={classes.graph}
          fit="contain"
          paddingX={theme.spacing(4)}
          paddingY={theme.spacing(4)}
          labelPosition={DependencyGraphTypes.LabelPosition.RIGHT}
          labelOffset={theme.spacing(1)}
          zoom="enable-on-click"
        />
      </div>
    </EntityInfoCard>
  );
}

/** The directions a graph can be laid out in, for the `direction` prop. */
export const Direction = DependencyGraphTypes.Direction;
